/**
 * Dated snapshots of what shipped on the RTL site.
 *
 * @remarks
 * These are not git tags. Each entry is a production deploy: `releasedOn` is
 * the day it went live, and later deploys the same day append to that entry.
 */

export interface ChangelogRelease {
  /** Calendar version, `YYYY.MM.DD`. */
  version: string
  /** `YYYY-MM-DD` the cluster went live. */
  releasedOn: string
  title: string
  changes: string[]
}

export const CHANGELOG: ChangelogRelease[] = [
  {
    version: '2026.10.22',
    releasedOn: '2026-10-22',
    title: 'Double overtime',
    changes: [
      'A game that needs a second overtime can be uploaded as two save states. The line score adds a 2OT column, and the box score combines both files. Extra v5 counters from the second save (tackles, 4th-down stops, and the rest) are ignored.',
      'Admins can download the original save state from Games, next to Edit and Void. Double-overtime results also offer the second file. Voided games stay on that list so they can still be downloaded, edited, or restored.',
      'League updates can include images. They show as clickable thumbnails; click one to see it full size. Admins can also attach files for download (schedules, workbooks, patches, and the rest).',
      'Defense tackles are tracked per player from season 34 on. They appear on the game box score and on both Defense leaderboards (league members and in-game players).',
    ],
  },
  {
    version: '2026.10.14',
    releasedOn: '2026-10-14',
    title: 'Late adds and luck',
    changes: [
      'The homepage signup button only appears between seasons, once the current season is complete.',
      'Admins can add a player after kickoff: pick a conference on the Season signups tab and they join that roster without rebuilding the split.',
      'Luck score is 100×net fumbles + 100×net FG blocks − 2×net tackles. Injuries no longer count.',
    ],
  },
  {
    version: '2026.10.13',
    releasedOn: '2026-10-13',
    title: 'Team box score',
    changes: [
      'Game pages show a twelve-stat team summary (offensive yards, special teams, TOP, tackles, 4th-down stops, fumbles, FG blocks) in place of the short totals strip. Injuries is no longer on that card.',
      'Leaderboards add Time of Possession, Takeaways, and Luck Score from season 34 on. Luck includes FG blocks at the same ±100 weight as a fumble.',
      'Admins can edit a league update after it is posted. Edited posts show the original date plus when they were last changed.',
      'Conference assignment uses the upcoming season’s signup pool only, then shows the AFC/NFC split side by side before it is saved.',
    ],
  },
  {
    version: '2026.10.12',
    releasedOn: '2026-10-12',
    title: 'Titles, updates, and this log',
    changes: [
      'A finished championship series now writes the Hall of Champions and the winner’s title history by itself — no separate Hall edit for Disciple in Season 33.',
      'League and season updates: the newest post sits under the homepage buttons, with a Remove control in Admin, and a dated archive at /updates.',
      'This changelog page, so the site’s own releases live in one place.',
    ],
  },
  {
    version: '2026.10.03',
    releasedOn: '2026-10-03',
    title: 'Postseason, polls, and save states',
    changes: [
      'Season status includes Postseason: games recorded while a season sits there are playoff games.',
      'Playoff brackets can be fixed or reseeded when they are created. A reseeded bracket redraws after every completed round.',
      'Official playoff games that uniquely match a live series attach themselves to that series, so the bracket updates when the games are entered.',
      'Conference assignment can be randomized: championship finalists are split, then the rest are tiered from the last three seasons of regular-season record (walking back if someone skipped a year). Players with fewer than three seasons total are left for the admin.',
      'Homepage poll under the hero buttons. Admin sets the question, options, and how long it stays open. One vote per Discord user; the page shows counts only.',
      'Poll options can be dragged to reorder in Admin.',
      'Compressed Nestopia UE save states parse the same way as the old uncompressed 13,165-byte files.',
    ],
  },
  {
    version: '2026.09.25',
    releasedOn: '2026-09-25',
    title: 'Playoff brackets',
    changes: [
      'Public Playoffs page with conference (AFC/NFC + championship) or unified brackets.',
      'Admin bracket builder: field, seeds from standings, series length (best of 1 or 3), notes.',
      'Series scores can be typed or counted from linked official games, including games entered from the series form.',
      'Byes, helmets, and round names drawn in place so the preview matches the live bracket.',
      'Records, standings, and Nerd Data can be scoped to regular season, playoffs, or all games.',
      'Historical brackets can be filled in for past seasons.',
    ],
  },
  {
    version: '2026.09.14',
    releasedOn: '2026-09-14',
    title: 'Season clock and rules',
    changes: [
      'Season lifecycle: preseason, active, and complete, set from Admin independently of which season is current for uploads.',
      'Midseason and end-of-season deadlines, with a games-played bar at midseason. Missed deadlines show on standings and cost tiebreakers.',
      'Rules page with the on-field and playoff rules as they stand.',
      'In-game player log: search a Tecmo roster name across parsed box scores.',
      'My Schedule for a signed-in player’s remaining and completed games.',
    ],
  },
  {
    version: '2026.09.01',
    releasedOn: '2026-09-01',
    title: 'Season 34 signups',
    changes: [
      'Homepage signup for the next season (always one past the current season).',
      'Admin signup pool: add or remove players by hand, then split them into conferences.',
      'Copy Discord tags from the homepage action row.',
    ],
  },
  {
    version: '2026.08.28',
    releasedOn: '2026-08-28',
    title: 'Thirty-two seasons of history',
    changes: [
      'Admin Excel import for seasons 1–32 (results sheet plus conference participants).',
      'Discord accounts imported from the old league so members keep the same identity.',
      'Import aliases so workbook names resolve to live players (Disciple, Nelson, and the rest).',
      'League display names, so the site can show the name the league actually uses.',
      'Player merge, with an audit trail, for split identities.',
      'Hall of Champions filled for every title from season 1 onward, including runner-up, series, and date where known.',
      'RSS feed of game results at /rss, in the same shape as the Prophet Games feed.',
      'Game recaps and blue-screen screenshot links from the season workbooks.',
    ],
  },
  {
    version: '2026.08.21',
    releasedOn: '2026-08-21',
    title: 'Season 33 launch',
    changes: [
      'RTL goes live: Home, Standings, Stats, Games, Upload, Hall of Champions, Historical, and Nerd Data.',
      'Discord sign-in. Admins are gated separately from regular members.',
      'Nestopia save-state upload and parse. Only a participant (or an admin) can finalize a game.',
      'AFC and NFC conferences, intra-conference round robin, standings with the league’s tiebreakers.',
      'Any of the 28 Tecmo teams on a given game, with helmets on tables and recaps.',
      'Player profiles: career record, season lines, rivalries, and box-score stats.',
      'Head-to-head records and all-time leaderboards on Nerd Data, including P1 share.',
      'Game pages with parsed box scores (passing, rushing, receiving, defense, kicking).',
      'Admin: users, conference assignments, game status, and manual results.',
      'Deployed on Railway with Discord OAuth and Postgres.',
    ],
  },
]
